import React from 'react';
import { observer } from 'mobx-react';
import { Emphasis } from '@estee/elc-base-theme';

import {
    TotalPriceItemWrapper,
    FreeItemLabel,
    TotalPrice,
    DiscountedPrice,
    OldPrice,
    PercentDiscount
} from '~views/ShoppingBagTab/CartSidebarItems/CartItem/CartItemPriceWrapper/CartItemPriceWrapperStyles';
import { useTranslation } from '~sharedComponents/Providers/Translations/TranslationsHook';

import { classes } from '~utils/ClassNames';
import { formatPriceWithCurrency } from '~utils/PriceFormatter';
import { diContainer } from '~setup/diContainer';
import { serviceNames } from '~setup/CartService';
import ITotalPriceItem from './ITotalPriceItem';
import { TTranslations } from '~views/Shared/Providers/Translations/TranslationsContext';

const cartClass = 'cart-item';
export const totalClass = classes('cart-item-total');
export const totalPriceWrapperClass = classes(`${cartClass}-total-price-wrapper`);
export const oldPriceClass = classes(`${cartClass}-old-total-price`);
export const freeClass = classes(`${cartClass}-free-price`);
export const sampleClass = classes(`${cartClass}-sample-price`);
export const percentClass = classes(`${cartClass}-percent`);
export const loyaltyClass = classes(`${cartClass}-loyalty-points`);
export const discountedTotalPriceClass = classes(`${cartClass}-discounted-total-price`);

const _translations = ['freeLabel', 'productSampleLabel', 'complimentaryLabel', 'pointsLabel'];

const TotalPriceItem: React.FC<ITotalPriceItem> = observer((props: ITotalPriceItem) => {
    const {
        skuId,
        productDiscount,
        discountedPrice = 0,
        originalPrice = 0,
        freeItem,
        freeSample,
        showDiscountedTotal,
        isLoyaltyReward,
        itemLoyaltyPoints
    } = props;

    const { config } = diContainer.get(serviceNames.config);
    const displayProductFreeLabel =
        config?.cartConfig?.productFreeLabel || config?.productFreeLabel;
    const displayProductSampleLabel =
        config?.cartConfig?.productSampleLabel || config?.productSampleLabel;
    const translations = useTranslation(_translations);
    const { freeLabel, pointsLabel } = translations || ({} as TTranslations);
    const hasOldPrice = originalPrice > discountedPrice;
    const priceFormatted = formatPriceWithCurrency(originalPrice);
    const maxDiscountAmount = 100;
    const hasFullDiscount = productDiscount === maxDiscountAmount;
    const showFreeLabel =
        (displayProductFreeLabel && (freeItem || hasFullDiscount)) ||
        (displayProductSampleLabel && freeSample);

    return (
        <TotalPriceItemWrapper
            className={totalPriceWrapperClass}
            data-testid={totalPriceWrapperClass}
        >
            {showFreeLabel ? (
                <FreeItemLabel className={freeClass} data-testid={totalClass}>
                    {freeLabel}
                </FreeItemLabel>
            ) : showDiscountedTotal && (productDiscount || hasOldPrice) ? (
                <>
                    <DiscountedPrice
                        className={`${totalClass} ${discountedTotalPriceClass}`}
                        data-testid={totalClass}
                    >
                        {formatPriceWithCurrency(discountedPrice)}
                    </DiscountedPrice>
                    <OldPrice
                        className={oldPriceClass}
                        emphasis={Emphasis.medium}
                        data-testid={oldPriceClass}
                    >
                        {priceFormatted}
                    </OldPrice>
                    {productDiscount && (
                        <PercentDiscount
                            className={percentClass}
                            data-testid={percentClass}
                            emphasis={Emphasis.medium}
                        >
                            {`(${productDiscount}%)`}
                        </PercentDiscount>
                    )}
                </>
            ) : isLoyaltyReward ? (
                <p className={loyaltyClass}>
                    {itemLoyaltyPoints} {pointsLabel}
                </p>
            ) : (
                <TotalPrice
                    id={`item-total-${skuId}`}
                    aria-live="assertive"
                    className={totalClass}
                    data-testid={totalClass}
                >
                    {priceFormatted}
                </TotalPrice>
            )}
        </TotalPriceItemWrapper>
    );
});

export default TotalPriceItem;
